import { createContext, useContext } from 'react';

export interface AbstractDropdownContextType {
  /** Asks the first item to focus. */
  focusFirstItem(): void;

  /** Asks the last item to focus. */
  focusLastItem(): void;

  /** Asks the item after the currently focused one to focus. */
  focusNextItem(currentItem: HTMLElement): void;

  /** Asks the item before the currently focused one to focus. */
  focusPreviousItem(currentItem: HTMLElement): void;

  /** Asks the toggle button to focus. */
  focusToggle(): void;

  /** Whether the menu is expanded. */
  isExpanded: boolean;

  /** The identifier attribute of the menu element. */
  itemsID: string;

  /** The style properties to apply to the menu element. */
  menuStyle: React.CSSProperties;

  /** Asks the item's popup to expand or collapse. */
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;

  /** Notifies when the menu is mounted. */
  setMenuElement: React.RefCallback<HTMLElement>;

  /** Notifies when the toggle button is mounted. */
  setToggleElement: React.RefCallback<HTMLElement>;

  /** The identifier attribute of the toggle button element. */
  toggleID: string;

  /** Whether the popup should collapse when one of its items is clicked. */
  shouldCollapseOnItemClick: boolean;
}

export const AbstractDropdownContext = createContext<AbstractDropdownContextType | null>(null);

export function useAbstractDropdownContext(consumerName: string, providerName = 'AbstractDropdown') {
  const context = useContext(AbstractDropdownContext);

  if (!context) {
    throw new Error(`<${consumerName}> may be used only in the context of an <${providerName}> component.`);
  }

  return context;
}
